import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import Dashboard from "./scenes/dashboard";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Drivers from "./scenes/drivers/Drivers";
import BalanceSheets from "./scenes/balanceSheets/BalanceSheets";
import Login from "./scenes/auth/login";
import Turnos from "./scenes/turnos/Turnos";
import TurnosLiquidados from "./scenes/turnos-liquidados/Turnos";
import TurnoDetalle from "./scenes/turnos/components/TurnoDetalle";
import NuevoTurno from "./scenes/turnos/NuevoTurno";
import Register from "./scenes/auth/register";
import { getStoredUserType, isAuthenticated } from "./services/authService";
import ServicioDetalle from "./scenes/turnos/components/ServicioDetalle";
import LiquidacionDetalle from "./scenes/balanceSheets/components/LiquidacionDetalle";
import UserProfile from "./scenes/user/user-profile";
import Analytics from "./scenes/analytics/Analytics";
import ServiciosAbonados from "./scenes/servicios-abonados/servicios-abonados";
import RegisterDriver from "./scenes/drivers/components/register-driver";
import HelpPage from "./scenes/help-page/HelpPage";
import Licenses from "./scenes/licenses/Licenses";
import LicenseRegister from "./scenes/licenses/components/LicenseRegister";
import LicenseEdit from "./scenes/licenses/components/LicenseEdit";
import VehicleRegister from "./scenes/vehicles/components/VehiclesRegister";
import Vehicles from "./scenes/vehicles/Vehicles";
import MaintenanceDetails from "./scenes/maintenances/MaintenanceDetails";
import VehicleDetails from "./scenes/vehicles/components/VehicleDetails";
import { listenForCsvIntent } from "./integrations/csvIntentListener";
import Heatmap from "./scenes/heatmap/Heatmap";
import Notifications from "./scenes/notifications/Notifications";
import Landing from "./scenes/landing/Landing";

const PUBLIC_ROUTES = ["/", "/login", "/register"];

const normalizePath = (path) => (path || "").replace(/\/+$/, "") || "/";

const getDefaultPathByRole = (role) => {
  if (!role) return "/login";
  if (role === "Driver") return "/turnos";
  return "/dashboard";
};

const ADMIN_ROLES = ["Boss", "LicenseUser"];
const DRIVER_ALLOWED = ["Boss", "Driver"];
const NOTIFICATION_ALLOWED = ["Boss", "Driver", "LicenseUser"];

const PrivateRoute = ({ element, allowedRoles }) => {
  const userType =
    useSelector((state) => state.user.type) || getStoredUserType();

  if (!isAuthenticated()) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles?.length && (!userType || !allowedRoles.includes(userType))) {
    return <Navigate to={getDefaultPathByRole(userType)} replace />;
  }

  return element;
};

function App() {
  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const userType =
    useSelector((state) => state.user.type) || getStoredUserType();
  const normalizedPath = useMemo(
    () => normalizePath(location.pathname),
    [location.pathname]
  );
  const isDashboard = normalizedPath === "/dashboard";

  const isPublicPage = PUBLIC_ROUTES.includes(normalizedPath);

  useEffect(() => {
    const removeCsvIntent = listenForCsvIntent((payload) => {
      console.log("[CsvIntent] App received payload", payload);
      navigate("/turnos", { state: { csvIntent: payload } });
    });

    return () => removeCsvIntent?.();
  }, [navigate]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {!isPublicPage ? (
          <div className="app">
            <Sidebar isSidebar={isSidebar} setIsSidebar={setIsSidebar} />
            <main className="content">
              <Topbar isSidebarOpen={isSidebar} setIsSidebar={setIsSidebar} />
              <div className={`page-content ${isDashboard ? "page-content--dashboard" : ""}`}>
                <Routes>
                  <Route
                    path="/dashboard"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<Dashboard />}
                      />
                    }
                  />
                  <Route
                    path="/drivers"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<Drivers />}
                      />
                    }
                  />
                  <Route
                    path="/drivers/register"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<RegisterDriver />}
                      />
                    }
                  />
                  <Route
                    path="/balanceSheets"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<BalanceSheets />}
                      />
                    }
                  />
                  <Route
                    path="/turnos"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<Turnos />}
                      />
                    }
                  />
                  <Route
                    path="/turnos/nuevo"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<NuevoTurno />}
                      />
                    }
                  />
                  <Route
                    path="/turnosliquidados"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<TurnosLiquidados />}
                      />
                    }
                  />
                  <Route
                    path="/turnos/:id"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<TurnoDetalle />}
                      />
                    }
                  />
                  <Route
                    path="/servicio/:id"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<ServicioDetalle />}
                      />
                    }
                  />
                  <Route
                    path="/liquidaciones/:id"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<LiquidacionDetalle />}
                      />
                    }
                  />
                  <Route
                    path="/user"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<UserProfile />}
                      />
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <PrivateRoute
                        allowedRoles={NOTIFICATION_ALLOWED}
                        element={<Notifications />}
                      />
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<Analytics />}
                      />
                    }
                  />
                  <Route
                    path="/heatmap"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<Heatmap />}
                      />
                    }
                  />
                  <Route
                    path="/servicios-abonados"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<ServiciosAbonados />}
                      />
                    }
                  />
                  <Route
                    path="/licenses"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<Licenses />}
                      />
                    }
                  />
                  <Route
                    path="/help"
                    element={
                      <PrivateRoute
                        allowedRoles={DRIVER_ALLOWED}
                        element={<HelpPage />}
                      />
                    }
                  />
                  <Route
                    path="/licenses/register"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<LicenseRegister />}
                      />
                    }
                  />
                  <Route
                    path="/licenses/:id/edit"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<LicenseEdit />}
                      />
                    }
                  />
                  <Route
                    path="/vehicles"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<Vehicles />}
                      />
                    }
                  />
                  <Route
                    path="/vehicles/register"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<VehicleRegister />}
                      />
                    }
                  />
                  <Route
                    path="/vehicles/:id"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<VehicleDetails />}
                      />
                    }
                  />
                  <Route
                    path="/maintenance/:id"
                    element={
                      <PrivateRoute
                        allowedRoles={ADMIN_ROLES}
                        element={<MaintenanceDetails />}
                      />
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <Navigate
                        to={isAuthenticated() ? getDefaultPathByRole(userType) : "/"}
                        replace
                      />
                    }
                  />
                </Routes>
              </div>
            </main>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
